import { styled } from '@linaria/react';
import { useContext } from 'react';
import { UserContext } from '../../../../contexts/user';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import {
  setFloraEffort as setFloraEffortInGlobalStore,
  setFloraModel as setFloraModelInGlobalStore,
} from '../../../../store/global-store/global-store.actions';
import { Select } from '../../../../ui-library/select/select';

/**
 * Super-user-only model and reasoning-effort pickers for the Flora agent.
 *
 * Renders nothing unless the authenticated user is a super-user, superuser
 * mode is enabled with the "Show Flora model selection" switch turned on, and
 * the backend returned a non-empty model catalog. Selecting a model resets the
 * effort to that model's default; the effort picker is hidden for models with
 * no valid efforts.
 *
 * @returns {JSX.Element | null} The selector controls, or null when hidden
 */
export function AgentModelSelector() {
  const { user } = useContext(UserContext);
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const floraModel = useGlobalStore((state) => state.floraModel);
  const floraEffort = useGlobalStore((state) => state.floraEffort);
  const floraModelsAvailable = useGlobalStore((state) => state.floraModelsAvailable);

  const showModelSelection =
    Boolean(user?.is_superuser) &&
    superuserMode.enabled &&
    superuserMode.showModelSelection &&
    floraModelsAvailable.length > 0;

  if (!showModelSelection) {
    return null;
  }

  const selectedModelOption = floraModelsAvailable.find((option) => option.model_id === floraModel);
  const showEffortSelection = Boolean(selectedModelOption && selectedModelOption.valid_efforts.length > 0);

  const handleModelChange = (value: string) => {
    setFloraModelInGlobalStore(value);
    const option = floraModelsAvailable.find((modelOption) => modelOption.model_id === value);
    setFloraEffortInGlobalStore(option?.default_effort ?? null);
  };

  return (
    <SelectorGroup>
      <Select
        data-bf="flora-model-select"
        radius="xl"
        size="xs"
        value={floraModel}
        options={floraModelsAvailable.map((option) => ({
          value: option.model_id,
          label: option.display_name,
        }))}
        onChange={handleModelChange}
      />
      {showEffortSelection && selectedModelOption && (
        <Select
          data-bf="flora-effort-select"
          radius="xl"
          size="xs"
          value={floraEffort ?? ''}
          options={selectedModelOption.valid_efforts.map((effort) => ({
            value: effort,
            label: effort,
          }))}
          onChange={setFloraEffortInGlobalStore}
        />
      )}
    </SelectorGroup>
  );
}

const SelectorGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;
